// src/pages/AdminCalendarPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../config";

/* ================= helpers ================= */
function pad2(n) {
  return String(n).padStart(2, "0");
}
function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}
function toISODateTimeLocal(d) {
  // for <input type="datetime-local" />
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mi = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfCalendarGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const day = first.getDay(); // 0..6 (Sun..Sat)
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - day);
  gridStart.setHours(0, 0, 0, 0);
  return gridStart;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function formatMonthTitle(d) {
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}
function safeDate(dt) {
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? null : d;
}
function asEventWhen(e) {
  return (
    e?.start ||
    e?.datetime ||
    e?.date ||
    e?.start_time ||
    e?.when ||
    e?.starts_at ||
    e?.startAt ||
    e?.start_at
  );
}
function eventTitle(e) {
  return e?.title || e?.name || e?.summary || "Event";
}
function eventType(e) {
  return (e?.type || e?.category || e?.kind || "other").toString().toLowerCase();
}
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

/* ✅ Step A: Safe JSON reader */
async function readJsonSafe(res) {
  if (!res) return null;

  let text = "";
  try {
    text = await res.text();
  } catch {
    return null;
  }

  const t = String(text || "").trim();
  if (!t) return null;

  try {
    return JSON.parse(t);
  } catch {
    return { error: "Non-JSON response", raw: t };
  }
}

/* ✅ same-origin image path helper (no API_BASE) */
function asImageUrl(p) {
  const s = String(p || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return s.startsWith("/") ? `${API_BASE}${s}` : `${API_BASE}/${s}`;
}

/* ✅ NEW: edit helpers */
function eventId(e) {
  return e?.id ?? e?.event_id ?? e?._id ?? null;
}
function toDTLocal(val) {
  const d = safeDate(val);
  return d ? toISODateTimeLocal(d) : "";
}

/* ================= modal ================= */
function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="cal-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cal-modal">
        <div className="cal-modal-head">
          <div className="cal-modal-title">{title}</div>
          <button className="cal-icon-btn" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="cal-modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ================= page ================= */
export default function AdminCalendarPage() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();

  const displayName = user?.name || user?.username || "Admin User";

  const [view, setView] = useState("month"); // month | week | day | list (UI only; Month is implemented)
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [selectedISO, setSelectedISO] = useState(() => toISODate(new Date()));

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const [openAdd, setOpenAdd] = useState(false);

  /* ✅ edit modal states */
  const [openEdit, setOpenEdit] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // form state
  const [fTitle, setFTitle] = useState("");
  const [fType, setFType] = useState("meeting");
  const [fDept, setFDept] = useState("");
  const [fStart, setFStart] = useState(() => toISODateTimeLocal(new Date()));
  const [fEnd, setFEnd] = useState(() => toISODateTimeLocal(addDays(new Date(), 0)));
  const [fNotes, setFNotes] = useState("");
  const [fImage, setFImage] = useState(null);

  const days = useMemo(() => {
    const gridStart = startOfCalendarGrid(monthDate);
    const arr = [];
    for (let i = 0; i < 42; i++) arr.push(addDays(gridStart, i));
    return arr;
  }, [monthDate]);

  const monthStart = useMemo(() => startOfMonth(monthDate), [monthDate]);
  const monthEnd = useMemo(() => endOfMonth(monthDate), [monthDate]);
  const todayISO = useMemo(() => toISODate(new Date()), []);

  // Load events for visible grid range (Month view range)
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setEventsLoading(true);
        setEventsError("");

        const gridStart = startOfCalendarGrid(monthDate);
        const from = toISODate(gridStart);
        const to = toISODate(addDays(gridStart, 41));

        const res = await authFetch(`/api/events?from=${from}&to=${to}`, { cache: "no-store" });
        const data = await readJsonSafe(res);

        if (!res.ok) {
          if (!alive) return;
          setEvents([]);
          setEventsError(data?.error || `Failed to load events (status ${res.status})`);
          return;
        }

        const list = Array.isArray(data) ? data : Array.isArray(data?.events) ? data.events : [];
        if (alive) setEvents(list);
      } catch (e) {
        if (alive) {
          setEvents([]);
          setEventsError(e?.message || "Failed to load events");
        }
      } finally {
        if (alive) setEventsLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [authFetch, monthDate]);

  // Group events by ISO day
  const eventsByDay = useMemo(() => {
    const map = new Map(); // iso -> events[]
    for (const e of events || []) {
      const when = asEventWhen(e);
      const d = safeDate(when);
      if (!d) continue;
      const iso = toISODate(d);
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(e);
    }

    // sort each day list by start time
    for (const [iso, list] of map.entries()) {
      list.sort((a, b) => {
        const da = safeDate(asEventWhen(a))?.getTime?.() ?? 0;
        const db = safeDate(asEventWhen(b))?.getTime?.() ?? 0;
        return da - db;
      });
      map.set(iso, list);
    }
    return map;
  }, [events]);

  function openAddForDate(iso) {
    const base = safeDate(`${iso}T09:00`);
    const start = base || new Date();
    const end = new Date(start);
    end.setHours(clamp(start.getHours() + 1, 0, 23), start.getMinutes(), 0, 0);

    setSelectedISO(iso);
    setFTitle("");
    setFType("meeting");
    setFDept("");
    setFStart(toISODateTimeLocal(start));
    setFEnd(toISODateTimeLocal(end));
    setFNotes("");
    setFImage(null);
    setSaveError("");
    setOpenAdd(true);
  }

  function openEditForEvent(ev, iso) {
    setSelectedISO(iso);
    setEditingEvent(ev);

    setFTitle(eventTitle(ev));
    setFType(eventType(ev) || "other");
    setFDept((ev?.department_id ?? ev?.department ?? "").toString());

    const st = asEventWhen(ev);
    setFStart(toDTLocal(st));

    const endVal = ev?.end || ev?.end_time || ev?.endAt || ev?.end_at || ev?.ends_at || "";
    setFEnd(endVal ? toDTLocal(endVal) : "");

    setFNotes((ev?.notes ?? "").toString());
    setFImage(null);

    setExistingImageUrl(ev?.image_url ? asImageUrl(ev.image_url) : "");

    setSaveError("");
    setOpenEdit(true);
  }

  async function saveEvent() {
    try {
      setSaving(true);
      setSaveError("");

      if (!fTitle.trim()) {
        setSaveError("Please enter an event title.");
        return;
      }

      const start = new Date(fStart);
      const end = new Date(fEnd);
      if (Number.isNaN(start.getTime())) {
        setSaveError("Invalid start date/time.");
        return;
      }
      if (!Number.isNaN(end.getTime()) && end < start) {
        setSaveError("End time cannot be before start time.");
        return;
      }

      const fd = new FormData();
      fd.append("title", fTitle.trim());
      fd.append("type", fType);
      fd.append("department_id", fDept.trim() || "");
      fd.append("start_time", start.toISOString());
      fd.append("end_time", Number.isNaN(end.getTime()) ? "" : end.toISOString());
      fd.append("notes", fNotes.trim() || "");

      if (fImage) fd.append("image", fImage);

      const res = await authFetch(`/api/events`, {
        method: "POST",
        body: fd,
      });

      const data = await readJsonSafe(res);

      if (!res.ok) {
        setSaveError(data?.error || `Failed to save event (status ${res.status})`);
        return;
      }

      setOpenAdd(false);

      const created = data?.event || data;
      if (created && (created?.title || created?.name || created?.start || created?.datetime || created?.date)) {
        setEvents((prev) => [...(Array.isArray(prev) ? prev : []), created]);
      } else {
        setMonthDate((d) => new Date(d));
      }
    } catch (e) {
      setSaveError(e?.message || "Failed to save event");
    } finally {
      setSaving(false);
    }
  }

  async function updateEvent() {
    try {
      setSaving(true);
      setSaveError("");

      if (!editingEvent) return;

      if (!fTitle.trim()) {
        setSaveError("Please enter an event title.");
        return;
      }

      const id = eventId(editingEvent);
      if (!id) {
        setSaveError("Event ID missing. Backend must return an id.");
        return;
      }

      const start = new Date(fStart);
      const end = fEnd ? new Date(fEnd) : null;

      if (Number.isNaN(start.getTime())) {
        setSaveError("Invalid start date/time.");
        return;
      }
      if (end && !Number.isNaN(end.getTime()) && end < start) {
        setSaveError("End time cannot be before start time.");
        return;
      }

      const fd = new FormData();
      fd.append("title", fTitle.trim());
      fd.append("type", fType);
      fd.append("department_id", fDept.trim() || "");
      fd.append("start_time", start.toISOString());
      fd.append("end_time", end ? end.toISOString() : "");
      fd.append("notes", fNotes.trim() || "");

      if (fImage) fd.append("image", fImage);

      const res = await authFetch(`/api/events/${id}`, {
        method: "PUT",
        body: fd,
      });

      const data = await readJsonSafe(res);
      if (!res.ok) {
        setSaveError(data?.error || `Failed to update event (status ${res.status})`);
        return;
      }

      const updated = data?.event || data;

      setEvents((prev) =>
        (Array.isArray(prev) ? prev : []).map((x) => (eventId(x) === id ? { ...x, ...updated } : x))
      );

      setOpenEdit(false);
      setEditingEvent(null);
      setExistingImageUrl("");
    } catch (e) {
      setSaveError(e?.message || "Failed to update event");
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvent() {
    try {
      setSaving(true);
      setSaveError("");

      const id = eventId(editingEvent);
      if (!id) {
        setSaveError("Event ID missing. Backend must return an id.");
        return;
      }

      const res = await authFetch(`/api/events/${id}`, { method: "DELETE" });
      const data = await readJsonSafe(res);

      if (!res.ok) {
        setSaveError(data?.error || `Failed to delete event (status ${res.status})`);
        return;
      }

      setEvents((prev) => (Array.isArray(prev) ? prev : []).filter((x) => eventId(x) !== id));
      setOpenEdit(false);
      setEditingEvent(null);
      setExistingImageUrl("");
    } catch (e) {
      setSaveError(e?.message || "Failed to delete event");
    } finally {
      setSaving(false);
    }
  }

  function typeChipStyle(type) {
    switch ((type || "other").toLowerCase()) {
      case "meeting":
        return {
          background: "rgba(37,99,235,.10)",
          color: "#1d4ed8",
          border: "1px solid rgba(37,99,235,.18)",
        };
      case "food":
        return {
          background: "rgba(245,158,11,.14)",
          color: "#92400e",
          border: "1px solid rgba(245,158,11,.22)",
        };
      case "training":
        return {
          background: "rgba(124,58,237,.12)",
          color: "#6d28d9",
          border: "1px solid rgba(124,58,237,.20)",
        };
      default:
        return {
          background: "rgba(34,197,94,.10)",
          color: "#15803d",
          border: "1px solid rgba(34,197,94,.16)",
        };
    }
  }

  return (
    <div className="cal-page">
      <style>{`
        :root{
          --bg:#f3f6fb;
          --card:#ffffff;
          --text:#0f172a;
          --muted:#64748b;
          --line:rgba(2,6,23,.08);
          --shadow:0 8px 18px rgba(2,6,23,.06);
          --radius:12px;
          --blue:#2563eb;
        }
        html, body { height: 100%; }

        .cal-page{
          min-height: calc(100dvh - 56px);
          background: var(--bg);
        }

        .main{
          min-height: 0;
          overflow: auto;
          padding: 14px 18px 18px;
          scrollbar-width: none;
        }
        .main::-webkit-scrollbar{ width:0; height:0; }

        .topbar{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 12px;
        }
        .page-title{
          font-size: 26px;
          font-weight: 950;
          color: var(--text);
          margin: 0;
        }
        .page-sub{
          margin-top: 4px;
          font-size: 13px;
          color: var(--muted);
          font-weight: 700;
        }
        .top-actions{
          display:flex;
          align-items:center;
          gap: 10px;
        }
        .icon-btn{
          height: 38px;
          width: 38px;
          border-radius: 10px;
          border: 1px solid var(--line);
          background: #fff;
          box-shadow: var(--shadow);
          cursor: pointer;
          font-weight: 900;
          color: var(--text);
        }
        .avatar{
          height: 38px;
          width: 38px;
          border-radius: 999px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: rgba(37,99,235,.12);
          color: var(--blue);
          border: 1px solid rgba(37,99,235,.18);
          font-weight: 950;
        }

        .cal-card{
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 14px;
          box-shadow: var(--shadow);
          overflow: hidden;
        }
        .cal-head{
          padding: 14px 16px;
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 14px;
          border-bottom: 1px solid rgba(2,6,23,.06);
        }
        .cal-head-left{
          display:flex;
          align-items:center;
          gap: 10px;
        }
        .cal-nav{
          display:flex;
          align-items:center;
          gap: 8px;
        }
        .cal-pill{
          border: 1px solid rgba(2,6,23,.08);
          background: #fff;
          height: 34px;
          padding: 0 12px;
          border-radius: 10px;
          font-weight: 900;
          color: var(--text);
          cursor:pointer;
        }
        .cal-pill.primary{
          background: rgba(37,99,235,.10);
          color: var(--blue);
          border: 1px solid rgba(37,99,235,.18);
        }
        .cal-icon-btn{
          height: 34px;
          width: 40px;
          border-radius: 10px;
          border: 1px solid rgba(2,6,23,.08);
          background: #fff;
          cursor: pointer;
          font-weight: 950;
          box-shadow: 0 6px 12px rgba(2,6,23,.06);
        }
        .cal-month-title{
          font-size: 28px;
          font-weight: 950;
          color: var(--text);
          text-align: center;
          margin: 0;
          min-width: 240px;
        }
        .cal-head-right{
          display:flex;
          align-items:center;
          gap: 10px;
        }
        .view-toggle{
          display:flex;
          border: 1px solid rgba(2,6,23,.08);
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
        }
        .view-btn{
          height: 34px;
          padding: 0 14px;
          border: none;
          background: transparent;
          font-weight: 900;
          color: var(--muted);
          cursor:pointer;
        }
        .view-btn.active{
          background: rgba(15,23,42,.60);
          color: #fff;
        }
          .view-btn:disabled{
          opacity: .5;
          cursor: not-allowed;
        }
        .add-btn{
          height: 34px;
          padding: 0 12px;
          border-radius: 12px;
          border: 1px solid rgba(37,99,235,.18);
          background: rgba(37,99,235,.10);
          color: var(--blue);
          font-weight: 950;
          cursor:pointer;
        }

        .cal-grid-wrap{
          padding: 10px 12px 12px;
        }
        .dow{
          display:grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 0;
          border-left: 1px solid rgba(2,6,23,.06);
          border-right: 1px solid rgba(2,6,23,.06);
        }
        .dow div{
          padding: 10px 10px;
          text-align:center;
          font-weight: 900;
          color: var(--text);
          border-top: 1px solid rgba(2,6,23,.06);
          border-bottom: 1px solid rgba(2,6,23,.06);
        }

        .month-grid{
          display:grid;
          grid-template-columns: repeat(7, 1fr);
          grid-auto-rows: 140px;
          gap: 0;
          border: 1px solid rgba(2,6,23,.06);
          border-top: none;
          border-radius: 12px;
          overflow: hidden;
        }
        .day-cell{
          background: #fff;
          border-right: 1px solid rgba(2,6,23,.06);
          border-bottom: 1px solid rgba(2,6,23,.06);
          padding: 8px;
          position: relative;
          cursor: pointer;
          transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
          overflow: hidden;

          display: flex;
          flex-direction: column;
        }

        .day-cell:nth-child(7n){
          border-right: none;
        }
        .day-cell:hover{
          background: rgba(37,99,235,.03);
          box-shadow: inset 0 0 0 2px rgba(37,99,235,.10);
        }

        .day-top{
          display:flex;
          justify-content: flex-end;
          align-items:center;
          gap: 8px;
          margin-bottom: 0px;
        }
        .day-num{
          font-weight: 950;
          color: var(--text);
          font-size: 14px;
          width: 28px;
          height: 28px;
          border-radius: 10px;
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .day-cell.outside .day-num{
          color: rgba(100,116,139,.55);
        }
        .day-cell.today .day-num{
          background: rgba(37,99,235,.10);
          color: var(--blue);
          border: 1px solid rgba(37,99,235,.18);
        }

        .day-cell.selected{
          background: rgba(37,99,235,.04);
          box-shadow: inset 0 0 0 2px rgba(37,99,235,.16);
          animation: selPulse .22s ease-out;
        }
        @keyframes selPulse{
          from{ box-shadow: inset 0 0 0 3px rgba(37,99,235,.28); }
          to{ box-shadow: inset 0 0 0 2px rgba(37,99,235,.16); }
        }

        .events-stack{
          display:flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
          min-height: 0;
        }

        .evt{
          font-size: 13px;
          font-weight: 900;
          padding: 6px 8px;
          border-radius: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          position: relative;

          color: #ffffff;
          background: rgba(0,0,0,.45);
          text-shadow: 0 2px 6px rgba(0,0,0,.8);
          transition: transform .14s ease, box-shadow .14s ease, filter .14s ease;
        }
        .evt:hover{
          transform: translateY(-1px);
          filter: brightness(1.02);
        }
        .evt:active{
          transform: translateY(0px) scale(.985);
        }

        .evt.has-img{
          position: relative;
          padding: 0;
          border-radius: 12px;
          overflow: hidden;
          background: #000;
          border: 1px solid rgba(2,6,23,.12);
          box-shadow: 0 6px 14px rgba(2,6,23,.10);

          flex: 1 1 auto;
          min-height: 0;
          transition: transform .14s ease, box-shadow .14s ease, filter .14s ease;
        }
        .evt.has-img:hover{
          transform: translateY(-1px);
          box-shadow: 0 10px 22px rgba(2,6,23,.18);
          filter: brightness(1.02);
        }
        .evt.has-img:active{
          transform: translateY(0px) scale(.985);
        }

        .evt-img{
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .evt-name{
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;

          height: 22px;
          display: flex;
          align-items: center;

          padding: 0 8px;
          font-size: 12px;
          font-weight: 900;

          color: #fff;
          background: linear-gradient(
            to top,
            rgba(0,0,0,.9),
            rgba(0,0,0,.4)
          );

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .evt-name .evt-time{
          margin-left: 8px;
          font-size: 11px;
          font-weight: 900;
          color: rgba(255,255,255,.85);
        }

        .evt small{
          font-weight: 900;
          opacity: .95;
          margin-left: 6px;
          color: #ffffff;
        }

        .more{
          font-size: 12px;
          font-weight: 900;
          color: var(--muted);
        }

        .hint{
          padding: 10px 14px 14px;
          color: var(--muted);
          font-weight: 800;
          font-size: 12px;
        }

        .cal-modal-backdrop{
          position: fixed;
          inset: 0;
          background: rgba(2,6,23,.45);
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 16px;
          z-index: 999;
        }
        .cal-modal{
          width: min(760px, 100%);
          background: #fff;
          border-radius: 14px;
          border: 1px solid rgba(2,6,23,.10);
          box-shadow: 0 24px 60px rgba(2,6,23,.25);
          overflow: hidden;
        }
        .cal-modal-head{
          padding: 12px 14px;
          border-bottom: 1px solid rgba(2,6,23,.06);
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 10px;
        }
        .cal-modal-title{
          font-weight: 950;
          font-size: 16px;
          color: var(--text);
        }
        .cal-modal-body{
          padding: 14px;
        }
        .form-grid{
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .field label{
          display:block;
          font-weight: 900;
          color: var(--text);
          font-size: 12px;
          margin-bottom: 6px;
        }
        .field input, .field select, .field textarea{
          width: 100%;
          border-radius: 12px;
          border: 1px solid rgba(2,6,23,.10);
          padding: 10px 12px;
          font-weight: 800;
          color: var(--text);
          outline: none;
        }
        .field textarea{
          resize: vertical;
          min-height: 90px;
        }
        .form-actions{
          display:flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 12px;
        }
        .btn{
          height: 40px;
          padding: 0 14px;
          border-radius: 12px;
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          font-weight: 950;
          cursor:pointer;
        }
        .btn.primary{
          background: var(--blue);
          color: #fff;
          border: 1px solid rgba(37,99,235,.35);
        }
        .err{
          background: rgba(239,68,68,.06);
          border: 1px solid rgba(239,68,68,.14);
          color: #991b1b;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 850;
          font-size: 12px;
          margin-bottom: 10px;
        }

        @media (max-width: 1099px){
          .cal-month-title{ font-size: 22px; min-width: auto; }
          .month-grid{ grid-auto-rows: minmax(96px, 1fr); }
          .form-grid{ grid-template-columns: 1fr; }
        }
                  @media (max-width: 1099px){
          .cal-month-title{ font-size: 22px; min-width: auto; }
          .month-grid{ grid-auto-rows: minmax(96px, 1fr); }
          .form-grid{ grid-template-columns: 1fr; }
        }

        /* ✅ ADD THIS BLOCK RIGHT HERE */
        @media (max-width: 640px){
          .main{ padding: 12px 12px 16px; }

          .topbar{
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 10px;
          }
          .top-actions{
            width: 100%;
            justify-content: flex-end;
          }

          .cal-head{
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }

          .cal-head-left{ justify-content: center; }

          .cal-month-title{
            order: 1;
            width: 100%;
            text-align: center;
            font-size: 22px;
            min-width: 0;
            line-height: 1.15;
          }

          .cal-head-right{
            order: 2;
            width: 100%;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 10px;
          }

          .view-toggle{
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            border-radius: 12px;
          }

          .view-btn{
            flex: 1 0 auto;
            padding: 0 10px;
            font-size: 12px;
            white-space: nowrap;
          }

          .add-btn{
            width: 100%;
            height: 38px;
            justify-content: center;
          }

          .cal-grid-wrap{
            padding: 10px;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .month-grid{ grid-auto-rows: 96px; }

          .dow div{
            padding: 8px 6px;
            font-size: 12px;
          }

          .day-cell{ padding: 6px; }

          .day-num{
            width: 26px;
            height: 26px;
            font-size: 13px;
          }

          .evt{
            font-size: 12px;
            padding: 5px 7px;
          }

          .evt-name{
            height: 20px;
            font-size: 11px;
          }
        }
      `}</style>

      <main className="main">
        <div className="topbar">
          <div>
            <h1 className="page-title">Calendar</h1>
            <div className="page-sub">Manage meetings, food events, and other activities · {displayName}</div>
          </div>

          <div className="top-actions">
            <button className="icon-btn" type="button" title="Back to Dashboard" onClick={() => navigate("/admin/dashboard")}>
              ⟵
            </button>
            <button className="icon-btn" type="button" title="Notifications" onClick={() => alert("Notifications later")}>
              🔔
            </button>
            <div className="avatar" title={displayName}>
              {displayName
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((x) => x[0]?.toUpperCase())
                .join("") || "AU"}
            </div>
          </div>
        </div>

        <div className="cal-card">
          <div className="cal-head">
            <div className="cal-head-left">
              <div className="cal-nav">
                <button
                  className="cal-icon-btn"
                  type="button"
                  onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
                  aria-label="Prev month"
                >
                  ‹
                </button>
                <button
                  className="cal-icon-btn"
                  type="button"
                  onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
                  aria-label="Next month"
                >
                  ›
                </button>
                <button
                  className="cal-pill primary"
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    setMonthDate(now);
                    setSelectedISO(toISODate(now));
                  }}
                >
                  Today
                </button>
              </div>
            </div>

            <h2 className="cal-month-title">{formatMonthTitle(monthDate)}</h2>

            <div className="cal-head-right">
              <div className="view-toggle" aria-label="Calendar view">
              {["month", "week", "day", "list"].map((k) => (
                <button
                  key={k}
                  type="button"
                  disabled={k !== "month"}
                  className={`view-btn ${view === k ? "active" : ""}`}
                  onClick={() => {
                    if (k === "month") setView(k);
                  }}
                >
                  {k[0].toUpperCase() + k.slice(1)}
                </button>
              ))}
              </div>

              <button className="add-btn" type="button" onClick={() => openAddForDate(selectedISO)}>
                ＋ Add event
              </button>
            </div>
          </div>

          <div className="cal-grid-wrap">
            {eventsError ? <div className="err">{eventsError}</div> : null}

            <div className="dow">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>

            <div className="month-grid">
              {days.map((d) => {
                const iso = toISODate(d);
                const outside = d < monthStart || d > monthEnd;
                const isToday = iso === todayISO;
                const isSelected = iso === selectedISO;

                const list = eventsByDay.get(iso) || [];
                const visible = list.slice(0, 2);
                const remaining = Math.max(0, list.length - visible.length);

                return (
                  <div
                    key={iso}
                    className={["day-cell", outside ? "outside" : "", isToday ? "today" : "", isSelected ? "selected" : ""].join(" ")}
                    onClick={() => setSelectedISO(iso)}
                    onDoubleClick={() => openAddForDate(iso)}
                    title="Click to select · Double-click to add event"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSelectedISO(iso);
                      if (e.key === " ") openAddForDate(iso);
                    }}
                  >
                    <div className="day-top">
                      <div className="day-num">{d.getDate()}</div>
                    </div>

                    <div className="events-stack">
                      {eventsLoading ? (isSelected ? <div className="more">Loading…</div> : null) : visible.length ? (
                        <>
                          {visible.map((ev, idx) => {
                            const st = safeDate(asEventWhen(ev));
                            const time = st ? `${pad2(st.getHours())}:${pad2(st.getMinutes())}` : "";
                            const imgPath = ev?.image_url ? asImageUrl(ev.image_url) : "";
                            const hasImg = !!imgPath;

                            return (
                              <div
                                key={`${iso}-${idx}`}
                                className={`evt ${hasImg ? "has-img" : ""}`}
                                style={hasImg ? undefined : typeChipStyle(eventType(ev))}
                                title={eventTitle(ev)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditForEvent(ev, iso);
                                }}
                              >
                                {hasImg ? (
                                  <>
                                    <img className="evt-img" src={imgPath} alt={eventTitle(ev)} />
                                    <div className="evt-name">
                                      <span>{eventTitle(ev)}</span>
                                      {time && <span className="evt-time">{time}</span>}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    {eventTitle(ev)}
                                    {time && <small>{time}</small>}
                                  </>
                                )}
                              </div>
                            );
                          })}

                          {remaining ? <div className="more">+{remaining} more</div> : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hint">
              Tip: <b>Double-click</b> any date to add an event. Events show inside the calendar cells and will also show in your dashboard “Upcoming Events” card (because both read from <code>/api/events</code>).
            </div>
          </div>
        </div>
      </main>

      {/* ADD EVENT MODAL */}
      <Modal
        open={openAdd}
        title={`Add event · ${selectedISO}`}
        onClose={() => {
          setOpenAdd(false);
          setSaveError("");
        }}
      >
        {saveError ? <div className="err">{saveError}</div> : null}

        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Upload Image (optional)</label>
          <input type="file" accept="image/*" onChange={(e) => setFImage(e.target.files?.[0] || null)} />
        </div>

        <div className="form-grid">
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Event title</label>
            <input value={fTitle} onChange={(e) => setFTitle(e.target.value)} placeholder="Meeting with team / Food event / etc." />
          </div>

          <div className="field">
            <label>Type</label>
            <select value={fType} onChange={(e) => setFType(e.target.value)}>
              <option value="meeting">Meeting</option>
              <option value="food">Food</option>
              <option value="training">Training</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="field">
            <label>Department (optional)</label>
            <input value={fDept} onChange={(e) => setFDept(e.target.value)} placeholder="Control Room / Gatekeeper / THAW ..." />
          </div>

          <div className="field">
            <label>Start</label>
            <input type="datetime-local" value={fStart} onChange={(e) => setFStart(e.target.value)} />
          </div>

          <div className="field">
            <label>End (optional)</label>
            <input type="datetime-local" value={fEnd} onChange={(e) => setFEnd(e.target.value)} />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Notes (optional)</label>
            <textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} placeholder="Add extra detail here..." />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn" type="button" onClick={() => setOpenAdd(false)} disabled={saving}>
            Cancel
          </button>
          <button className="btn primary" type="button" onClick={saveEvent} disabled={saving}>
            {saving ? "Saving..." : "Save event"}
          </button>
        </div>
      </Modal>

      {/* EDIT EVENT MODAL */}
      <Modal
        open={openEdit}
        title={`Edit event · ${selectedISO}`}
        onClose={() => {
          setOpenEdit(false);
          setEditingEvent(null);
          setExistingImageUrl("");
          setSaveError("");
        }}
      >
        {saveError ? <div className="err">{saveError}</div> : null}

        {existingImageUrl ? (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 900, fontSize: 12, color: "#0f172a", marginBottom: 6 }}>Current Image</div>
            <img
              src={existingImageUrl}
              alt="Current"
              style={{
                width: "100%",
                maxHeight: 180,
                objectFit: "cover",
                borderRadius: 12,
                border: "1px solid rgba(2,6,23,.10)",
              }}
            />
          </div>
        ) : null}

        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Replace Image (optional)</label>
          <input type="file" accept="image/*" onChange={(e) => setFImage(e.target.files?.[0] || null)} />
        </div>

        <div className="form-grid">
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Event title</label>
            <input value={fTitle} onChange={(e) => setFTitle(e.target.value)} />
          </div>

          <div className="field">
            <label>Type</label>
            <select value={fType} onChange={(e) => setFType(e.target.value)}>
              <option value="meeting">Meeting</option>
              <option value="food">Food</option>
              <option value="training">Training</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="field">
            <label>Department (optional)</label>
            <input value={fDept} onChange={(e) => setFDept(e.target.value)} />
          </div>

          <div className="field">
            <label>Start</label>
            <input type="datetime-local" value={fStart} onChange={(e) => setFStart(e.target.value)} />
          </div>

          <div className="field">
            <label>End (optional)</label>
            <input type="datetime-local" value={fEnd} onChange={(e) => setFEnd(e.target.value)} />
          </div>

          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label>Notes (optional)</label>
            <textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} />
          </div>
        </div>

        <div className="form-actions" style={{ justifyContent: "space-between" }}>
          <button className="btn" type="button" onClick={deleteEvent} disabled={saving}>
            {saving ? "..." : "Delete"}
          </button>

          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn" type="button" onClick={() => setOpenEdit(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn primary" type="button" onClick={updateEvent} disabled={saving}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}